import { z } from "zod";
import { EXERCISE_PROMPT_MAX, ExerciseToken } from "./exercises.ts";
import { PageBox } from "./page-link.ts";

/**
 * Контракт извлечения заданий из PDF преподавателя
 * (`docs/exercise-extraction-design.md`, разделы 4-6, 8.3 и решение 15) и его
 * пути картинки (`docs/scan-extraction-design.md`).
 *
 * ⚠️ **Ключей ответа здесь нет ни одного поля, и это барьер раздела 10, а не
 * упущение.** Модель только раскладывает напечатанный текст по полям, верные
 * варианты отмечает человек. Поэтому файл живёт в корне `@edu/shared`, а не за
 * подпутём `@edu/shared/exercises`: он едет в браузер преподавателя и ученику
 * не открывает ничего.
 */

/**
 * Потолки ответа модели. Заданий на страницу не больше 20 (раздел 6).
 *
 * ⚠️ Длины берутся у `choice`, а не пишутся второй рукой: условие это
 * `EXERCISE_PROMPT_MAX`, текст варианта это поле `ExerciseToken`. Для числа
 * вариантов именованной константы у `choice` нет (`.min(2).max(10)` литералами
 * в `exercises.ts` и `exercises-authoring.ts`), поэтому числа стоят здесь, а
 * расхождение с элементом `choice` ловит замок в
 * `apps/api/src/extract/parse.test.ts`.
 */
export const EXTRACT_MAX_TASKS = 20;
export const EXTRACT_OPTIONS_MIN = 2;
export const EXTRACT_OPTIONS_MAX = 10;

/**
 * Ответ модели (раздел 6): условие и варианты с маркером числом.
 *
 * ⚠️ Объекты строгие: лишнее поле это не «срежем молча», а `malformed`
 * (решение 15). Поле под ключ, номер задания или тему модель вернуть не может
 * вовсе, разбор такой ответ не примет.
 *
 * ⚠️ `U+FFFE` в тексте схема НЕ запрещает намеренно: это отказ одного
 * задания замком (`checkPage`), а не всего ответа (раздел 6).
 */
export const ExtractOption = z.strictObject({
  marker: z.number().int(),
  text: ExerciseToken.shape.text,
});
export const ExtractTask = z.strictObject({
  prompt: z.string().max(EXERCISE_PROMPT_MAX),
  options: z.array(ExtractOption).min(EXTRACT_OPTIONS_MIN).max(EXTRACT_OPTIONS_MAX),
});
export const ExtractReply = z.strictObject({
  tasks: z.array(ExtractTask).max(EXTRACT_MAX_TASKS),
});
export type ExtractOption = z.infer<typeof ExtractOption>;
export type ExtractTask = z.infer<typeof ExtractTask>;
export type ExtractReply = z.infer<typeof ExtractReply>;

/**
 * Что уезжает к прокси, и больше ничего (раздел 5).
 *
 * ⚠️ **Отдельный тип на границе, а не соглашение** (5.9 CLAUDE.md): ни id
 * заготовки, ни файла, ни владельца, ни времени. Только текст страницы, в
 * котором `U+FFFE` заменён на дефис и перевод строки (`orderOf` в
 * `apps/api/src/extract/prompt.ts`).
 */
export const ExtractOrder = z.strictObject({ pageText: z.string() });
export type ExtractOrder = z.infer<typeof ExtractOrder>;

/**
 * Схемы ОТВЕТОВ ручек ниже это `z.object`, а не `z.strictObject`
 * (`docs/scan-extraction-design.md`, раздел 12): строгая схема роняет разбор у
 * старого бандла на первом же новом поле, а открытая вкладка сама не
 * перезагружается (5.1 CLAUDE.md). Новые поля пути картинки шаг 2 завёл
 * необязательными (он выкатывался раньше сервера, который их отдаёт), шаг 3
 * сделал их обязательными вместе с сервером. Вкладка шага 2 читает их через
 * `??` и обязательность не замечает. Ответ модели и тип на границе выше
 * остаются строгими: там лишнее поле это `malformed`, а не терпимость.
 */

/**
 * Сколько слайдов займёт одна страница пути картинки, вместе с её убираемым
 * слайдом (раздел 16: 10,9 на страницу с заданиями, разброс 2-19). Окно запуска
 * считает место по этому числу минус один убираемый слайд страницы.
 *
 * Здесь, в корне, а не за подпутём с ответами: число читает окно запуска,
 * которому раскладка заданий не нужна.
 */
export const SCAN_SLIDES_PER_PAGE = 11;

/**
 * Оценка цены страницы пути картинки в кредитах, пока у владельца меньше
 * десяти замеров (раздел 4): по ней окно запуска считает «Разбор займёт около
 * N кредитов ИИ». Здесь, а не на сервере, потому что то же число нужно вкладке,
 * пока ответ с медианой не пришёл (`ExtractLatestResponse.scanCreditsPerPage`).
 */
export const SCAN_CREDITS_DEFAULT = 4;

/** Путь страницы: текстовый слой или картинка (решение 2). */
export const ExtractVia = z.enum(["text", "image"]);
export type ExtractVia = z.infer<typeof ExtractVia>;

/**
 * Абзац окна запуска про границу, дословно из раздела 4. Виден ВСЕГДА, а не
 * условно (5.9 CLAUDE.md). Точки в конце строки нет по правилу раздела 4.
 */
export const EXTRACT_BORDER_TEXT =
  "Текст отмеченных страниц мы отправим нейросети, которая работает за границей: " +
  "она соберёт из него задания с проверкой. Верные ответы отметите вы. Если на " +
  "странице есть имена учеников или их работы, снимите с неё галочку";

/**
 * Тот же абзац, когда среди отмеченных есть страницы пути картинки (раздел 4
 * `docs/scan-extraction-design.md`): туда уходит страница целиком, а ответы
 * предлагает нейросеть.
 */
export const EXTRACT_SCAN_BORDER_TEXT =
  "Отмеченные страницы мы отправим нейросети, которая работает за границей. " +
  "Она соберёт задания и предложит ответы, а где не сможет, ответы поставите вы. " +
  "Если на странице есть имена учеников или их работы, снимите с неё галочку";

/** Абзац про границу по составу отмеченных страниц. */
export function extractBorderText(anyImage: boolean): string {
  return anyImage ? EXTRACT_SCAN_BORDER_TEXT : EXTRACT_BORDER_TEXT;
}

/**
 * Итог страницы (`extract_page.outcome`, 8.3).
 *
 * `no_exercises` это страница пути картинки, уже разобранная с итогом «заданий
 * нет» (раздел 6 `docs/scan-extraction-design.md`): окно запуска её больше не
 * предлагает. Живёт здесь, а не только в итогах окна, потому что запуск пишет
 * итог окна строкой `done`, как `already_added`.
 */
export const ExtractOutcome = z.enum([
  "tasks",
  "no_tasks",
  "no_text",
  "no_markers",
  "already_added",
  "retry_limit",
  "failed",
  "no_exercises",
]);
export type ExtractOutcome = z.infer<typeof ExtractOutcome>;

/**
 * Класс отказа вызова (решение 15). Повторы и потолки решает таблица решения.
 *
 * Пути картинки (решения 1 и 7 и раздел 6 `docs/scan-extraction-design.md`):
 * `quota` не хватило места в материалах под картинку страницы, `render`
 * страницу не удалось отрисовать, `off` разбор картинкой выключен списком,
 * `scan_rejected` отвергнуты все упражнения ответа. Первые три без повтора,
 * последний с явным повтором.
 */
export const ExtractFailureClass = z.enum([
  "down",
  "rejected",
  "truncated",
  "malformed",
  "credits",
  "quota",
  "render",
  "off",
  "scan_rejected",
]);
export type ExtractFailureClass = z.infer<typeof ExtractFailureClass>;

/**
 * Имя замка, отвергшего задание (7.3; в `failure`, когда отвергнуты все).
 *
 * ⚠️ Имя, а не причина прототипа: причина цитирует строку страницы, а в базу
 * и в ответ ручки текст страницы ради отказа не пишется.
 */
export const ExtractLock = z.enum([
  "fffe_in_reply",
  "markers_not_sequential",
  "marker_in_option",
  "prompt_not_found",
  "marker_seam",
  "option_not_verbatim",
  "prompt_mid_line",
  "foreign_marker_line",
  "answer_line_in_span",
  "task_number_in_span",
  "marker_mid_line",
  "next_task_in_last_option",
  "answer_in_last_option",
  "option_cut_mid_word",
  "marker_after_last_option",
]);
export type ExtractLock = z.infer<typeof ExtractLock>;

/** `failure` страницы: класс вызова или имя замка. */
export const ExtractFailure = z.union([ExtractFailureClass, ExtractLock]);
export type ExtractFailure = z.infer<typeof ExtractFailure>;

/**
 * Строки шва (раздел 4) из исходных знаков страницы, до 60 знаков.
 *
 * `prev` есть только у первого задания страницы и только если предыдущая
 * страница файла есть; `next` так же у последнего. Пустая строка у `prev` и
 * `next` означает, что строки нет; у `before` и `after` это «ничего нет».
 */
export const ExtractSeams = z.object({
  prev: z.string().optional(),
  before: z.string(),
  after: z.string(),
  next: z.string().optional(),
});
export type ExtractSeams = z.infer<typeof ExtractSeams>;

/**
 * Пометка ДЕФИС-ПЕРЕНОС: в каком поле (`"prompt"` или номер варианта 1..n) и
 * какое слово выделить.
 *
 * ⚠️ Слово берётся из СКЕЛЕТА текста модели (`textSkeleton`), а не из сырого
 * ответа: кавычки там одним знаком `"`, тире дефисом, минус числа знаком
 * `U+2212`, многоточие тремя точками. Пунктуация и кавычки по краям слова
 * сняты, буквы, цифры и внутренний дефис остаются. Модель разорвала слово
 * пробелом («пере нос»): в `word` обе части через пробел.
 */
export const ExtractHyphenMark = z.object({
  field: z.union([z.literal("prompt"), z.number().int().min(1)]),
  word: z.string().min(1),
});
export type ExtractHyphenMark = z.infer<typeof ExtractHyphenMark>;

/**
 * Принятое задание страницы, как оно лежит в `extract_page.tasks` (8.3).
 *
 * Текст условия и вариантов собран из исходных знаков СТРАНИЦЫ, а не модели
 * (7.5). `hyphenMarks` это пометки ДЕФИС-ПЕРЕНОС (раздел 4, «под вариантом»
 * и выделенное слово), `fileNo` это свой номер задания для подписи «Задание 3
 * в файле» (решение 17), `null`: подписи нет.
 */
export const ExtractedTask = z.object({
  id: z.string().min(1),
  prompt: z.string(),
  options: z.array(z.object({ marker: z.number().int(), text: z.string() })),
  hyphenMarks: z.array(ExtractHyphenMark),
  seams: ExtractSeams,
  fileNo: z.string().nullable(),
});
export type ExtractedTask = z.infer<typeof ExtractedTask>;

/**
 * Страница задачи и сама задача в ответе ручек (раздел 9).
 *
 * ⚠️ Состав этих двух схем заведён шагом 2 под ручки шага 3 и уточняется
 * вместе с ними: значения `status` и счётчики разбора в проекте не названы
 * поимённо.
 */
export const ExtractPageStatus = z.enum(["queued", "running", "done"]);
export type ExtractPageStatus = z.infer<typeof ExtractPageStatus>;

/**
 * Отрисовка чтения, которую видела модель (решение 21): с неё режется кусок
 * страницы и на неё ставится привязка задания скана. Размер nullable, как у
 * `PageLink`.
 */
export const ExtractReadPicture = z.object({
  // uuid, как у `PageLink.fileId`: из этого значения вкладка ставит привязку, и
  // не-uuid дал бы отказ источника у каждого задания при прошедшем замке сервера
  fileId: z.string().uuid(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});
export type ExtractReadPicture = z.infer<typeof ExtractReadPicture>;

/** Общий текст страницы скана: ключ, на который ссылаются упражнения, и рамка. */
export const ExtractPassage = z.object({ key: z.string(), box: PageBox });
export type ExtractPassage = z.infer<typeof ExtractPassage>;

export const ExtractPage = z.object({
  slideId: z.string(),
  fileId: z.string(),
  status: ExtractPageStatus,
  outcome: ExtractOutcome.nullable(),
  failure: ExtractFailure.nullable(),
  tasks: z.array(ExtractedTask),
  /** Сколько заданий ответа отвергнуто замками: «одно задание разобрать не получилось». */
  rejected: z.number().int().nonnegative(),
  /** Строки мягкой проверки 7.3: цепочек вариантов больше, чем заданий в ответе. */
  soft: z.number().int().nonnegative(),
  /**
   * Уходила ли страница к модели в ЭТОЙ задаче. Для счёта «Разбираем
   * страницы: 5 из 13»: итог, записанный при запуске (`false`), не путается с
   * итогом захвата. Считается так:
   *
   * - строка не `done` (`queued` или `running`): отправленная, `true`, даже если
   *   исполнитель её ещё не брал;
   * - строка `done` и `attempts > 0`: её захватывали, `true`;
   * - строка `done` и `attempts = 0`: итог записан при запуске, `false`.
   *
   * ⚠️ `attempts` строки только растёт. Захват, упёршийся в потолок захватов
   * строки (`retry_limit`), счётчик не двигает, и признак там держится на том,
   * что до потолка эту строку уже брали: потолок держит только автоматические
   * захваты, а их без первого захвата не бывает. `retry_limit`, записанный при
   * запуске, бывает только у строк задач до 21.10.2026.
   */
  sent: z.boolean(),
  /**
   * Поля пути картинки (раздел 12 `docs/scan-extraction-design.md`),
   * обязательные с шага 3: сервер отдаёт их у каждой страницы, у старого пути
   * пустыми.
   *
   * `sourceFileId` и `pageNo` это исходный PDF и номер страницы в нём (`null`
   * только у строк разбора до миграции шага 3), `pageLabel` напечатанный номер
   * страницы (решение 17). `read` это отрисовка чтения, которую видела модель,
   * `null` у старого пути и до захвата. `scanIds` это id заданий каждого
   * упражнения скана по его раскладке, `rejectedTasks` число отвергнутых
   * упражнений. Сами упражнения скана лежат в `scan` НЕРАЗОБРАННЫМИ: в них
   * ответы, а этот модуль едет в стартовый чанк; разбирает их ленивый модуль за
   * подпутём `@edu/shared/exercises` (раздел 13). У старого пути `scan` и
   * `scanIds` пустые, его задания в `tasks`.
   */
  via: ExtractVia,
  sourceFileId: z.string().nullable(),
  pageNo: z.number().int().nonnegative().nullable(),
  pageLabel: z.string().nullable(),
  read: ExtractReadPicture.nullable(),
  passages: z.array(ExtractPassage),
  scanIds: z.array(z.array(z.string())),
  rejectedTasks: z.number().int().nonnegative(),
  scan: z.array(z.unknown()),
});
export type ExtractPage = z.infer<typeof ExtractPage>;

export const ExtractJob = z.object({
  id: z.string(),
  planId: z.string(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  pages: z.array(ExtractPage),
});
export type ExtractJob = z.infer<typeof ExtractJob>;

/**
 * Запуск разбора (`POST /v1/plans/:planId/extractions`, раздел 9).
 * `clientKey` это uuid: в базе ключ уникален парой с владельцем и лежит
 * колонкой `uuid`. `slideIds` это отмеченные галочками слайды окна запуска;
 * незнакомые и слайды без подложки страницы просто не считаются.
 */
export const ExtractStartRequest = z.strictObject({
  clientKey: z.string().uuid(),
  slideIds: z.array(z.string().min(1).max(200)).min(1).max(200),
  /**
   * Исходные файлы, которые преподаватель подтвердил как учебный материал по
   * английскому без работ учеников (решение 20). Чужой uuid сервер не пишет.
   */
  confirmSources: z.array(z.string().uuid()).max(200).optional(),
});
export type ExtractStartRequest = z.infer<typeof ExtractStartRequest>;

/**
 * Страница окна запуска (раздел 4): `outcome: null` уйдёт к модели, иначе причина.
 *
 * ⚠️ `retry_limit` среди причин окна нет с 21.10.2026 (решение 15 проекта
 * извлечения, изменено решением владельца): потолка по паре больше нет, и
 * окно запуска его не даёт. В `ExtractOutcome` значение остаётся: его носят
 * строки старых задач и ставит потолок захватов строки.
 */
export const ExtractLaunchOutcome = ExtractOutcome.extract(["no_text", "no_markers", "already_added", "no_exercises"]);
export type ExtractLaunchOutcome = z.infer<typeof ExtractLaunchOutcome>;
/**
 * Поля пути картинки обязательные с шага 3 (шапка схем ответов выше):
 * `sourceFileId`, `sourceName` и `pageNo` это исходный файл, его имя для
 * группы окна запуска и номер страницы в нём (решение 17), `confirmed`
 * подтверждён ли файл как учебный материал (решение 20). У страницы старого
 * пути `confirmed: true`: подтверждать там нечего, страница уходит текстом.
 * `null` у трёх полей файла не бывает у страницы, найденной по подложке, и
 * оставлен ради формы, общей со страницей задачи.
 *
 * `parsedBefore`: этот владелец уже получал ответ модели по этому файлу
 * страницы в любой живущей задаче (пара (владелец, файл страницы), решения 11
 * и 15). Ничего не запрещает: окно запуска по нему предупреждает, что новый
 * разбор снова потратит кредиты ИИ. Сервер шлёт его всегда; `.default(false)`
 * стоит ради окна выкатки, когда новая вкладка читает ответ старого сервера.
 */
export const ExtractLaunchPage = z.object({
  slideId: z.string(),
  fileId: z.string(),
  outcome: ExtractLaunchOutcome.nullable(),
  via: ExtractVia,
  sourceFileId: z.string().nullable(),
  sourceName: z.string().nullable(),
  pageNo: z.number().int().nonnegative().nullable(),
  confirmed: z.boolean(),
  parsedBefore: z.boolean().default(false),
});
export type ExtractLaunchPage = z.infer<typeof ExtractLaunchPage>;

/**
 * Ответ `GET .../extractions/latest` (раздел 9): `job: null`, если разбора не
 * было; `pages` только без живой задачи.
 *
 * `scanCreditsPerPage` это оценка цены одной страницы пути картинки в
 * кредитах, дробью до сотой (раздел 4 `docs/scan-extraction-design.md`, строка
 * «Разбор займёт около 20 кредитов ИИ»): медиана по последним страницам скана
 * владельца, пока замеров меньше десяти, 4. Считает её сервер, потому что
 * вкладка видит только последнюю задачу своей заготовки. Окно умножает число на
 * отмеченные страницы скана; страницы старого пути в оценку не входят. Поле
 * обязательное с шага 3; вкладка шага 2 его не знает и не читает.
 */
export const ExtractLatestResponse = z.object({
  job: ExtractJob.nullable(),
  pages: z.array(ExtractLaunchPage).optional(),
  scanCreditsPerPage: z.number().nonnegative(),
});
export type ExtractLatestResponse = z.infer<typeof ExtractLatestResponse>;

/**
 * Ответ запуска (201 новая задача, 200 повтор того же `clientKey`) и повтора
 * страницы: задача целиком.
 */
export const ExtractJobResponse = z.object({ job: ExtractJob });
export type ExtractJobResponse = z.infer<typeof ExtractJobResponse>;
